import clsx from 'clsx';
import React from 'react';

import { type Length, VerticalAlignment } from '@gitbook/api-client';
import { usePaletteHandle } from '@gitbook/react-uikit';
import type { BlockProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { BlockWrapper } from '../common';
import { ColumnWidthContext } from './BlockColumns';
import { useColumnBlockPaletteFactory } from './palette';
import { transformLengthToCSS } from './utils';

export function BlockColumn(props: BlockProps) {
    const columnWidths = React.useContext(ColumnWidthContext);
    const index = props.parent.getChildIndex(props.node.key);
    const data = props.node.data;
    const width: Length = data?.get('width') ?? { value: columnWidths[index] * 100, unit: '%' };
    const verticalAlignment: VerticalAlignment | undefined = data?.get('verticalAlignment');
    const { className, style } = transformLengthToCSS(width) ?? {};
    return (
        <div
            className={clsx(
                'group/column relative flex w-full flex-col',
                /* need space for content block controls */
                !props.editor.readOnly && 'pr-4 pl-8',
                {
                    'justify-start':
                        verticalAlignment === VerticalAlignment.Top || !verticalAlignment,
                    'justify-center': verticalAlignment === VerticalAlignment.Middle,
                    'justify-end': verticalAlignment === VerticalAlignment.Bottom,
                },
                className
            )}
            style={style}
        >
            {props.editor.readOnly ? null : <ColumnOptionsButton nodeKey={props.node.key} />}
            <BlockWrapper {...props} />
        </div>
    );
}

function ColumnOptionsButton(props: { nodeKey: string }) {
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(buttonRef);
    const palette = useColumnBlockPaletteFactory({ nodeKey: props.nodeKey });

    const onPress = useEventCallback(() => {
        paletteHandle.open(palette());
    });

    return (
        <Button
            ref={buttonRef}
            onPress={onPress}
            active={paletteHandle.opened}
            className={clsx(
                '-top-2 absolute right-2 z-10 opacity-0 transition-opacity group-focus-within/column:opacity-100 group-hover/column:opacity-100 group-focus/column:opacity-100',
                paletteHandle.opened ? 'opacity-100' : 'opacity-0'
            )}
            iconOnly
            leadingIcon={Icon.MoreVertical}
            label="Column options"
            size="small"
            kind="secondary"
        />
    );
}
